import Vue from 'vue';
import store from '@/store';
import { getDateWithoutTimePart, getTodayDate, nextDate } from '@/helpers';
import PickUpDateTime from '@/models/pickUpDateTime.model';
import PickUpTime from '@/models/pickUpTime.model';

export default class AvailableDateService {
  constructor() {
    this.disableRangeCache = null;
    this.pickUpRangeCache = new Map();
    this.disabledDates = {
      dates: [],
    };
    this.getDisableRange().then((disableRange) => {
      this.disabledDates = { ...this.disabledDates, ...disableRange };
    });
  }

  /**
   *
   * @return {Promise<void>}
   */
  getDisableRange() {
    let { disableRangeCache } = this;

    if (!disableRangeCache) {
      const today = getTodayDate();
      const todayUpBookingDay = new Date();

      todayUpBookingDay.setDate(today.getDate() + 7);

      disableRangeCache = {
        to: getDateWithoutTimePart(today),
        from: getDateWithoutTimePart(todayUpBookingDay),
      };
    }

    return Promise.resolve(disableRangeCache);
  }

  /**
   * Получение доступных интервалов вызова курьера
   * @param date - Date - дата вызова курьера
   * @param pickUpLocation - pickUpLocation - адрес куда вызвать курьера
   * @return {Promise<PickUpDateTime>}
   */
  async getAvailableTimesForDate(
    date,
    pickUpLocation,
    considerWeekend = false,
  ) {
    const { pickUpRangeCache } = this;
    const dateKey = date.toString();
    let pickUpRange;
    const { windowsTimeZoneId } = pickUpLocation;

    const { locationFrom, locationTo } = store.state.preparation;
    const {
      CityAlias: cityFrom,
      CountryCode: countryCodeFrom,
      PostalCode: postalCodeFrom,
      autoPostal: autoPostalFrom,
    } = this.changePostfixNeeded(locationFrom);
    const localFrom = {
      cityFrom,
      countryCodeFrom,
      postalCodeFrom: postalCodeFrom || autoPostalFrom,
    };

    const {
      CityAlias: cityTo,
      CountryCode: countryCodeTo,
      PostalCode: postalCodeTo,
      autoPostal: autoPostalTo,
    } = this.changePostfixNeeded(locationTo);
    const localTo = {
      cityTo,
      countryCodeTo,
      postalCodeTo: postalCodeTo || autoPostalTo,
    };

    try {
      let timesRanges = await Vue.api.calculatePickupRanges({
        ...localFrom,
        ...localTo,
        windowsTimeZoneId,
        date,
      });

      timesRanges = timesRanges.map((item) => {
        const { From, To, DeadLine } = item;
        return {
          From,
          To,
          Date: date,
          DayType: null,
          DeadLine: DeadLine || null,
        };
      });

      if (considerWeekend === true) {
        const weekendDays = store.getters['preparation/filterWeekendDay'](date);
        const keysWeekendDays = Object.keys(weekendDays);
        // eslint-disable-next-line no-restricted-syntax
        for (const key of keysWeekendDays) {
          const day = weekendDays[key];
          timesRanges.push({
            From: day.from,
            To: day.to,
            Date: day.date,
            DayType: day.dayType,
            DeadLine: day.deadLine,
          });
        }
      }

      const times = timesRanges.map((t) => {
        const { From, To, Date: dateInstance, DayType, DeadLine } = t;
        const [hoursFrom, minutesFrom] = From.split(':');
        const [hoursTo, minutesTo] = To.split(':');

        const d = new Date(dateInstance);

        const timeFrom = new Date(d.setHours(+hoursFrom, +minutesFrom, 0));
        const timeTo = new Date(d.setHours(+hoursTo, +minutesTo, 0));

        return new PickUpTime(
          timeFrom,
          timeTo,
          windowsTimeZoneId,
          dateInstance,
          DayType,
          DeadLine,
        );
      });

      pickUpRange = new PickUpDateTime(date, times, times.length > 0);
      pickUpRangeCache.set(dateKey, pickUpRange);
    } catch (e) {
      throw new Error(e);
    }

    return Promise.resolve(pickUpRange);
  }

  async getFirstAvailableTime() {
    const dayTimes = await this.getPickUpRanges();
    if (!dayTimes.isAvailable) {
      store.dispatch(
        'preparation/setPickUpDate',
        nextDate(store.state.preparation.pickUpDate),
      );
      return this.getFirstAvailableTime();
    }
    return store.dispatch('preparation/setPickUpRange', dayTimes.times[0]);
  }

  getPickUpRanges(considerWeekend = false) {
    store.dispatch('preparation/clearPickUpRange');
    return this.getAvailableTimesForDate(
      store.state.preparation.pickUpDate,
      store.getters['preparation/pickUpAddress'],
      considerWeekend,
    ).then((availableTimes) => {
      store.dispatch('preparation/setPickUpRange', null);
      store.dispatch('preparation/setPickUpAvailableTimes', availableTimes);
      if (!availableTimes.isAvailable) {
        this.disabledDates.dates.push(new Date(availableTimes.date));
        store.dispatch('preparation/setPickUpRange', null);
      }
      store.dispatch('preparation/setPickUpRange', availableTimes.times[0]);
      return availableTimes;
    });
  }

  async getWeekend() {
    const { locationFrom, locationTo } = store.state.preparation;
    const {
      CityAlias: cityFrom,
      CountryCode: countryCodeFrom,
      PostalCode: postalCodeFrom,
      autoPostal: autoPostalFrom,
    } = this.changePostfixNeeded(locationFrom);
    const localFrom = {
      cityFrom,
      countryCodeFrom,
      postalCodeFrom: postalCodeFrom || autoPostalFrom,
    };

    const {
      CityAlias: cityTo,
      CountryCode: countryCodeTo,
      PostalCode: postalCodeTo,
      autoPostal: autoPostalTo,
    } = this.changePostfixNeeded(locationTo);
    const localTo = {
      cityTo,
      countryCodeTo,
      postalCodeTo: postalCodeTo || autoPostalTo,
    };

    const date = store.state.preparation.pickUpDate;
    const [dateFormat] = date.toISOString().split('T');

    const { windowsTimeZoneId } = store.getters['preparation/pickUpAddress'];

    const weekendRanges = await Vue.api.calculatePickupRangesForWeekends({
      localFrom,
      localTo,
      date: dateFormat,
      timeZone: windowsTimeZoneId,
    });

    const newWeekendRanges = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const weekendDay of weekendRanges) {
      const { Date: dateString, DayType, DeadLine, From, To } = weekendDay;
      const [month, day, year] = dateString.split('/');
      const dateDay = new Date(year, month - 1, day);
      newWeekendRanges[dateDay.getTime()] = {
        date: dateDay,
        dayType: DayType,
        deadLine: DeadLine,
        from: From,
        to: To,
      };
    }

    store.dispatch('preparation/setPickUpAvailableWeekend', {
      date,
      weekendRanges: newWeekendRanges,
    });

    return newWeekendRanges;
  }

  // eslint-disable-next-line class-methods-use-this
  changePostfixNeeded(local) {
    let postalCode = local.PostalCode || local.autoPostal;
    if (local.PostfixNeeded === 1) {
      postalCode += local.Postfix;
    }

    return {
      ...local,
      PostalCode: postalCode,
    };
  }
}
